import { motion, AnimatePresence } from "framer-motion";

const CurrentWeather = ({ data }) => {
  const temperature = Math.round(data?.main?.temp);
  const condition = data?.weather[0]?.main;
  const iconCode = data?.weather[0]?.icon;

  // Weather-based recommendations
  const getRecommendation = () => {
    if (typeof condition !== "string") return "Check the weather before heading out!";
    const cond = condition.toLowerCase();
    if (cond.includes("rain")) return "Bring an umbrella, raincoat, and waterproof shoes.";
    if (cond.includes("snow")) return "Wear a heavy coat, boots, gloves, and a warm hat.";
    if (cond.includes("clear")) {
      if (temperature >= 28) return "Wear light, breathable clothes, sunglasses, and a hat.";
      if (temperature >= 18) return "A t-shirt and jeans or a dress are perfect today.";
      if (temperature >= 10) return "A light jacket or sweater is recommended.";
      if (temperature < 10) return "Wear a warm jacket and consider a scarf.";
      return "Great day for outdoor activities!";
    }
    if (cond.includes("cloud")) {
      if (temperature >= 20) return "A long-sleeve shirt or light sweater is good for cloudy weather.";
      if (temperature >= 10) return "A sweater or light jacket is recommended.";
      return "Wear a warm jacket and layers for the chill.";
    }
    if (cond.includes("thunder")) return "Stay indoors if possible. If outside, wear a raincoat and avoid metal objects.";
    if (cond.includes("wind")) return "Wear a windbreaker or jacket to stay comfortable.";
    if (temperature >= 30) return "Stay hydrated, wear a hat, sunglasses, and light clothing.";
    if (temperature >= 20) return "Short sleeves and light pants or shorts are ideal.";
    if (temperature >= 10) return "A sweater or light jacket is a good idea.";
    if (temperature <= 5) return "Dress warmly: coat, scarf, gloves, and hat recommended.";
    return "Have a nice day! Dress comfortably.";
  };

  // Helper to get animated background class based on weather
  const getBackgroundClass = () => {
    if (typeof condition !== "string") return ""; // No background while loading
    const cond = condition.toLowerCase();
    if (cond.includes("rain")) return "animated-bg rain-bg";
    if (cond.includes("snow")) return "animated-bg snow-bg";
    if (cond.includes("clear")) return "animated-bg clear-bg";
    if (cond.includes("cloud")) return "animated-bg cloud-bg";
    if (cond.includes("thunder")) return "animated-bg thunder-bg";
    return "bg-gradient-to-r from-blue-200 to-blue-400";
  };

  return (
    <div className={`relative flex items-center gap-4 pl-0 p-4 mt-16 rounded-xl overflow-hidden min-h-[120px] ${getBackgroundClass()}`}>
      {/* Animated Weather Background Overlays */}
      <AnimatePresence mode="wait">
        <motion.div
          key={condition}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0 z-0 pointer-events-none"
        >
          {/* Background overlays handled by CSS classes */}
        </motion.div>
      </AnimatePresence>
      <div className="weather-icon z-10">
        {iconCode && (
          <motion.img
            key={iconCode}
            src={`https://openweathermap.org/img/wn/${iconCode}@2x.png`}
            alt={condition}
            className="w-16 h-16 drop-shadow-2xl shadow-lg bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] rounded-lg"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </div>
      <div className="z-10">
        <div className="flex items-baseline gap-2">
          <motion.h2
            className="text-4xl font-bold"
            key={condition + temperature}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            {condition}, {temperature}°
          </motion.h2>
          <span className="text-gray-400 text-2xl">Today</span>
        </div>
        <div className="mt-2 flex gap-2 items-center">
          <button
            onClick={() => {
              const url = window.location.href;
              navigator.clipboard.writeText(url);
              alert("Link copied to clipboard!");
            }}
            className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
            title="Copy link to clipboard"
          >
            Copy Link
          </button>
          <motion.span
            className="text-sm text-green-700 bg-green-50 rounded px-2 py-1"
            key={getRecommendation()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {getRecommendation()}
          </motion.span>
        </div>
      </div>
    </div>
  );
};

export default CurrentWeather;
